import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui";
import { actionsKeys, permissionActions } from "@/lib/utils";
import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useDeleteCommunityMutation,
  useGetCommunities,
  useGetCommunityDeleteState,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Building, Edit, Landmark, MoreVertical, User2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useCommunities = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const accessPermission = useSelector((state: RootState) => state.currentData.accessPermission);

  const hasDocReadPermission = useMemo(() => accessPermission[actionsKeys.DOCUMENT_TYPE?.toLowerCase()][permissionActions.READ], [accessPermission]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  //get communities
  const { data: communitiesData, isLoading: isLoadingCommunities } =
    useGetCommunities({});

  const {
    data: communityDeleteState,
    isLoading: isLoadingCommunityDeleteState,
  } = useGetCommunityDeleteState({
    id: deletedRow?.communityId,
    queryParams: { enabled: !!deletedRow?.communityId },
  });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteCommunityMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["communities"],
        });
        navigate(`/${routes.cam}/${routes.communities}`);
        toast.success("Community Deleted Successfully");
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "displayId",
      header: "ID",
      accessorFn: (row: any) => String(row.displayId),
    },
    {
      accessorKey: "name",
      header: "Name",
      accessorFn: (row: any) => String(row?.name || ""),
    },
    {
      accessorKey: "generalPhoneNo",
      header: (
        <div className=" w-full">
          General <br /> Contact Phone
        </div>
      ),
      accessorFn: (row: any) => String(row?.generalPhoneNo || ""),
    },
    {
      accessorKey: "emergencyPhoneNo",
      header: (
        <div className=" w-full">
          Emergency <br /> Contact Phone
        </div>
      ),
      accessorFn: (row: any) => String(row?.emergencyPhoneNo || ""),
    },
    {
      accessorKey: "city",
      header: "City",
      accessorFn: (row: any) => String(row?.city || ""),
    },
    {
      accessorKey: "state",
      header: "State",
      accessorFn: (row: any) => String(row?.state || ""),
    },
    {
      accessorKey: "totalProperties",
      header: "# of Properties",
      cell: ({ row }: any) => {
        return row.original.totalProperties ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setCommunityData(row.original));
              navigate(`${row.original.communityId}/${routes.properties}`);
            }}
            className="text-blue-500 hover:text-blue-600 cursor-pointer px-3"
          >
            {String(row.original.totalProperties)}
          </div>
        ) : (
          <div className="px-3">{"0"}</div>
        );
      },
    },
    {
      accessorKey: "totalUsers",
      header: "# of Users",
      cell: ({ row }: any) => {
        return row?.original?.totalUsers ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setCommunityData(row.original));
              navigate(`${row.original.communityId}/${routes.users}`);
            }}
            className="text-blue-500 hover:text-blue-600 cursor-pointer px-3"
          >
            {String(row?.original?.totalUsers)}
          </div>
        ) : (
          <div className="px-3">{"0"}</div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(`${row.row.original.communityId}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(
                    row.row.original.communityId + "/" + routes.properties
                  );
                }}
              >
                <Building className="w-4 h-4" />
                Manage property
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className="gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <Landmark className="h-4 w-4" /> Financials
                </DropdownMenuSubTrigger>

                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-52">
                    {hasDocReadPermission ? <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        className="gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        Configuration
                      </DropdownMenuSubTrigger>

                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="w-52">
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              dispatch(setCommunityData(row.row.original));
                              navigate(
                                row.row.original.communityId +
                                  "/" +
                                  routes.documentTypeConfig
                              );
                            }}
                          >
                            Document Type
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub> : <></>}
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch(setCommunityData(row.row.original));
                        navigate(
                          row.row.original.communityId + "/" + routes.budgetMgmt
                        );
                      }}
                    >
                      Budget Management
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(
                    row.row.original.communityId + "/" + routes.vendorMgmt
                  );
                }}
              >
                <User2 className="w-4 h-4" />
                Vendor Management
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onConfirm = (reason: string) => {
    deleteInfo({ id: deletedRow?.communityId, reason });
    onClose();
  };

  const onRowClick = (row: any) => {
    dispatch(setCommunityData(row.original));
    navigate(`${row.original.communityId}/view`);
  };

  return {
    globalFilter,
    setGlobalFilter,
    columns,
    communitiesData,
    isLoadingCommunities,
    onClose,
    openDialog,
    isDeletePending,
    onConfirm,
    communityDeleteState,
    isLoadingCommunityDeleteState,
    deletedRow,
    onRowClick,
  };
};

export default useCommunities;
